import {
  Button,
  Divider,
  Flex,
  Group,
  Modal,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useCallback } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { usePostApi } from "src/hooks";
import { getSearchScopeParams } from "src/pages/SearchPage/SearchPage.utils";
import { useSelector } from "src/redux/hooks";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

interface IExportForm {
  email: string;
  name: string;
  notes: string;
}

const ExportButton = () => {
  const [opened, { open, close }] = useDisclosure();
  const toExport = useSelector(state => state.searchSlice.toExport);
  const uppercase = useSelector(state => state.preferencesSlice.uppercase);
  const { collectionId } = useParams();
  const [searchParams] = useSearchParams();
  const { register, handleSubmit } = useForm<IExportForm>();
  const { post, isLoading } = usePostApi();

  const onSubmit: SubmitHandler<IExportForm> = useCallback(
    async data => {
      await post({
        request: "export-entries",
        body: {
          params: {
            yachtid: getYachtId(),
            list: toExport,
            ...getSearchScopeParams(searchParams.get("type"), collectionId),
            ...data,
          },
        },
        onSuccess: () => {
          Notification(
            "Success",
            `This has now been sent to ${data.name}`,
            "green",
          );
          close();
        },
      });
    },
    [close, collectionId, post, searchParams, toExport],
  );
  return (
    <>
      <Button variant="outline" disabled={toExport.length === 0} onClick={open}>
        Export
      </Button>
      <Modal size="lg" opened={opened} onClose={close} title="Export Entries">
        <Text c="text">
          Entries to export: {toExport.length}. Please enter the email to send
          them to!
        </Text>
        <Divider mt="sm" mb="xl" />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex direction="column" mt="xl" gap="sm" w="80%" mx="auto">
            <Flex align="center">
              <Text w="30%" c="text">
                Persons Name:
              </Text>
              <TextInput
                w="70%"
                {...register("name", { required: true })}
                styles={{
                  input: { textTransform: uppercase ? "uppercase" : "none" },
                }}
              />
            </Flex>
            <Flex align="center" justify="stretch">
              <Text w="30%" c="text">
                Email:
              </Text>
              <TextInput
                w="70%"
                {...register("email", { required: true })}
                styles={{
                  input: { textTransform: uppercase ? "uppercase" : "none" },
                }}
              />
            </Flex>
            <Flex justify="stretch" direction="column" gap="xs">
              <Text c="text">Medical Notes:</Text>
              <Textarea
                autosize
                minRows={4}
                maxRows={10}
                {...register("notes", { required: true })}
                styles={{
                  input: { textTransform: uppercase ? "uppercase" : "none" },
                }}
              />
            </Flex>
          </Flex>
          <Group mt="xl" justify="right">
            <Button type="submit" loading={isLoading}>
              Export
            </Button>
          </Group>
        </form>
      </Modal>
    </>
  );
};

export default ExportButton;
